import {
  Box,
  Button,
  FormControlLabel,
  Icon,
  Stack,
  Switch,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Assessment, DATE_FORMAT_SHORT_FNS } from '@schooly/api';
import { PageHeaderSearchInput } from '@schooly/components/filters';
import { RecurrenceInfo } from '@schooly/components/recurring';
import { AssessmentStatuses } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  ArrowLeftIcon,
  CheckboxSquareIconButton,
  DoubleCheckIcon,
  EmptyFolderSvg,
  GridCell,
  MainPageGrid,
  ReportGeneratedIcon,
  ReportsIcon,
  SkeletonRows,
  TypographyWithOverflowHint,
  VirtualizedList,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, MouseEventHandler, PropsWithChildren, useCallback } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { MarkbookFilterForm } from '../../../../../context/markbook/MarkbookFilterContext';
import { AssessmentReportsTooltip } from '../../../AssessmentReportsTooltip';
import { MethodCellContent } from '../../../AssessmentsGrid';
import { PAGE_SIZE } from '../helpers';
import { StepWrapper } from '../MarkbookFilterModal';

export const SKELETON_COLS = 5;

type AdjustManuallyStepProps = {
  onGoBack: () => void;
  entries: Assessment[];
  isLoading: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
};

export const AdjustManuallyStep: FC<PropsWithChildren<AdjustManuallyStepProps>> = ({
  onGoBack,
  entries,
  isLoading,
  fetchNextPage,
  isFetchingNextPage,
  children,
}) => {
  const { watch, setValue } = useFormContext<MarkbookFilterForm>();

  const selectedAssessmentIds = watch('selectedAssessmentIds');

  const handleSelectRow = useCallback(
    (id: string) => {
      const prev = selectedAssessmentIds;
      let next: typeof prev;
      if (prev === 'all') {
        next = new Set(
          entries.reduce<string[]>((acc, next) => (next.id === id ? acc : [...acc, next.id]), []),
        );
      } else {
        next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
      }
      setValue('selectedAssessmentIds', next);
    },
    [entries, selectedAssessmentIds, setValue],
  );

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        pb: 0.25,
      }}
    >
      {children}
      {isLoading && <SkeletonGridLoader columnsCount={SKELETON_COLS} amount={PAGE_SIZE} />}
      {!isLoading && !entries.length && <MarkbookEditNoAssessmentsStub onGoBack={onGoBack} />}

      <VirtualizedList
        items={entries}
        renderItem={(index, assessment) => (
          <AssessmentRow
            key={assessment.id}
            assessment={assessment}
            handleSelect={handleSelectRow}
            isSelected={
              selectedAssessmentIds
                ? selectedAssessmentIds === 'all' || selectedAssessmentIds.has(assessment.id)
                : false
            }
          />
        )}
        endReached={fetchNextPage}
        footer={
          isFetchingNextPage ? (
            <SkeletonGridLoader columnsCount={SKELETON_COLS} amount={PAGE_SIZE} />
          ) : null
        }
      />
    </Stack>
  );
};

type AssessmentRowProps = {
  assessment: Assessment;
  isSelected: boolean;
  handleSelect: (assessmentId: string) => void;
};

const AssessmentRow: FC<AssessmentRowProps> = ({ assessment, isSelected, handleSelect }) => {
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();
  const { formatMessage } = useIntl();

  const autoGeneratedFromReport = assessment.autogenerated;
  const hasReports = !!assessment.reports.length;
  const showReportIcon = autoGeneratedFromReport || hasReports;
  const showPublishedIcon = assessment.status === AssessmentStatuses.Published;

  const recurrenceId = assessment.recurring_state?.recurrence_id;
  const recurringState = assessment.recurring_state;
  const showRecurrenceIcon = !!recurringState && !!recurrenceId;

  const onSelect: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      handleSelect?.(assessment.id);
    },
    [assessment.id, handleSelect],
  );

  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  return (
    <TableRow
      sx={(theme) => ({
        '.checkbox': {
          transition: 'opacity .2s',
          position: 'absolute',
        },
        '&:hover td': {
          backgroundColor: theme.palette.background.default,
        },
        display: 'flex',
        width: '100%',
      })}
    >
      <GridCell borderBottom sx={{ flex: '0 0 46px' }} pl={2.6} pr={0.5}>
        <CheckboxSquareIconButton isActive={isSelected} onClick={onSelect} />
      </GridCell>

      <GridCell borderBottom sx={{ flex: '0 0 60px' }} px={0}>
        <Typography color="common.grey" textAlign="center">
          {format(
            newDateTimezoneOffset(assessment.assessment_date),
            DATE_FORMAT_SHORT_FNS,
          ).toUpperCase()}
        </Typography>
      </GridCell>

      <GridCell borderBottom sx={{ flex: '1 1 auto', minWidth: 0 }} pr={3}>
        <Stack flexDirection="row" gap={1}>
          <TypographyWithOverflowHint variant="h3" color="common.grey2" noWrap>
            {assessment.name}
          </TypographyWithOverflowHint>
          {showRecurrenceIcon && <RecurrenceInfo recurringState={recurringState} />}
          {showReportIcon && (
            <AssessmentReportsTooltip assessment={assessment}>
              <Icon sx={{ color: 'common.grey2' }}>
                {assessment.autogenerated ? <ReportGeneratedIcon /> : <ReportsIcon />}
              </Icon>
            </AssessmentReportsTooltip>
          )}
          {showPublishedIcon && (
            <Tooltip
              title={
                <Typography variant="caption">
                  {formatMessage({ id: 'status-Published' })}
                </Typography>
              }
              componentsProps={{
                tooltip: {
                  sx: (theme) => ({
                    padding: theme.spacing(0, 0.75),
                  }),
                },
              }}
              arrow={false}
            >
              <Icon sx={(theme) => ({ '& .svg-icon': { color: theme.palette.success.main } })}>
                <DoubleCheckIcon />
              </Icon>
            </Tooltip>
          )}
        </Stack>
      </GridCell>

      <GridCell borderBottom sx={{ flex: '0 0 160px', minWidth: 0 }} pl={0}>
        <TypographyWithOverflowHint
          color={evaluationTitle ? 'common.grey2' : 'common.grey'}
          className={evaluationTitle ? undefined : 'hoverTextSecondary'}
        >
          {evaluationTitle ?? '-'}
        </TypographyWithOverflowHint>
      </GridCell>

      <GridCell borderBottom sx={{ flex: '0 0 120px' }} px={0}>
        <MethodCellContent assessment={assessment} />
      </GridCell>
    </TableRow>
  );
};

type SelectAllProps<T> = {
  selectedIds: T;
  total: number;
  onSelectAll: () => void;
  selectedCount: number;
  isOverLimit: boolean;
  limit: number;
};

const SelectAll = <T extends 'all' | Set<string>>({
  selectedIds,
  total,
  selectedCount,
  isOverLimit,
  limit,
  onSelectAll,
}: SelectAllProps<T>) => {
  const { formatMessage } = useIntl();

  const isAllSelected = selectedIds === 'all' || (!!total && selectedCount === total);

  const isNotSelected = selectedIds !== 'all' && !selectedCount;

  const getLabel = () => {
    if (isNotSelected) {
      return formatMessage({ id: 'assessments-markbook-selectAll' });
    }
    if (isAllSelected) {
      return formatMessage(
        { id: 'countOfTypeSelected' },
        {
          count: formatMessage({ id: 'filter-All' }),
          userTypePlural: formatMessage({ id: 'groups-Assessments' }),
        },
      );
    }
    return formatMessage(
      { id: 'countOfTypeSelected' },
      {
        count: selectedCount,
        userTypePlural: formatMessage({
          id: selectedCount > 1 || selectedCount === 0 ? 'groups-Assessments' : 'groups-Assessment',
        }),
      },
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            border: '1px solid',
            borderRadius: '6px',
            borderColor: (theme) => (isOverLimit ? theme.palette.error.main : 'transparent'),
          }}
        >
          <CheckboxSquareIconButton
            isActive={isAllSelected}
            disabled={!total}
            onClick={onSelectAll}
          />
        </Box>

        <Typography variant="h3" color={isNotSelected ? 'common.grey' : undefined}>
          {getLabel()}
        </Typography>
      </Stack>

      <Typography
        pl={3.5}
        variant="caption"
        color="error"
        sx={{ visibility: isOverLimit ? 'visible' : 'hidden' }}
      >
        {formatMessage({ id: 'assessments-markbook-OverLimit' }, { count: limit })}
      </Typography>
    </Box>
  );
};

type SkeletonGridLoaderProps = {
  columnsCount: number;
  amount: number;
};

const SkeletonGridLoader = ({ columnsCount, amount }: SkeletonGridLoaderProps) => {
  return (
    <MainPageGrid
      sx={{
        '& .MuiTableCell-root': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider} !important`,
        },
      }}
    >
      <SkeletonRows columnsCount={columnsCount} amount={amount} />
    </MainPageGrid>
  );
};

type MarkbookEditNoAssessmentsStubProps = {
  onGoBack: () => void;
};

const MarkbookEditNoAssessmentsStub: FC<MarkbookEditNoAssessmentsStubProps> = ({ onGoBack }) => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      pt={6}
      gap={1}
      justifyContent="center"
      alignItems="center"
      position="relative"
      height="100%"
    >
      <Box
        sx={{
          svg: {
            width: 300,
          },
        }}
      >
        <EmptyFolderSvg />
      </Box>
      <Typography variant="h1" maxWidth={300} textAlign="center" pt={7}>
        {formatMessage({ id: 'assessments-markbook-noAssessments' })}
      </Typography>
      <Typography variant="h3" color="text.primary" textAlign="center">
        {formatMessage({ id: 'assessments-markbook-noAssessmentsDescription' })}
      </Typography>
      <Button startIcon={<ArrowLeftIcon />} onClick={onGoBack} sx={{ mt: 2 }}>
        {formatMessage({ id: 'assessments-markbook-noAssessmentsButton' })}
      </Button>
    </Stack>
  );
};

type SelectAssessmentHeaderProps = {
  query: string;
  hasAssessments: boolean;
  onQueryChange: (value: string) => void;
  limit: number;
  total: number;
  selectedCount: number;
  isOverLimit: boolean;
  showRecurring: boolean;
  onShowRecurringChange: (value: boolean) => void;
};

export const SelectAssessmentHeader: FC<SelectAssessmentHeaderProps> = ({
  query,
  hasAssessments,
  onQueryChange,
  limit,
  total,
  selectedCount,
  isOverLimit,
  showRecurring,
  onShowRecurringChange,
}) => {
  const { formatMessage } = useIntl();
  const { setValue, watch } = useFormContext<MarkbookFilterForm>();
  const selectedAssessmentIds = watch('selectedAssessmentIds');

  const onSelectAll = useCallback(() => {
    if (!total) return;
    if (selectedAssessmentIds === 'all') {
      setValue('selectedAssessmentIds', new Set());
    } else {
      setValue('selectedAssessmentIds', 'all');
    }
  }, [selectedAssessmentIds, setValue, total]);
  return (
    <StepWrapper
      pt={2.5}
      position="relative"
      gap={2.5}
      sx={(theme) => ({
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        zIndex: theme.zIndex.drawer,
        borderBottom: '1px solid',
        borderColor: 'divider',
        alignItems: 'center',
      })}
    >
      <PageHeaderSearchInput
        value={query || ''}
        disabled={!hasAssessments && !query}
        onChangeText={onQueryChange}
        placeholder={formatMessage({ id: 'assessments-Search' })}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        pl={2.5}
        pr={0.5}
        width="100%"
        alignItems="flex-start"
      >
        <SelectAll
          limit={limit}
          selectedIds={selectedAssessmentIds}
          total={total}
          onSelectAll={onSelectAll}
          selectedCount={selectedCount}
          isOverLimit={isOverLimit}
        />

        <FormControlLabel
          control={
            <Switch
              checked={showRecurring}
              onChange={(_, value) => {
                onShowRecurringChange(value);
              }}
            />
          }
          label={formatMessage({ id: 'action-ShowRecurring' })}
          sx={{
            '& .MuiFormControlLabel-label': {
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Stack>
    </StepWrapper>
  );
};
